"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import { splitSentenceStrings } from "@/lib/tool-kit/text-analysis/sentence-count";

const RE_WORDS = /\s+/;

/** English strings. A translated content file overrides these through `ui`. */
export const SENTENCE_COUNTER_STRINGS = {
  textareaAriaLabel: "Text to count sentences in",
  placeholder: "Paste or type your text here...",
  sentences: "Sentences",
  avgWordsPerSentence: "Avg Words / Sentence",
  longestSentence: "Longest Sentence",
  shortestSentence: "Shortest Sentence",
  wordsCount: "one:{count} word|other:{count} words",
  sentenceLengthBreakdown: "Sentence Length Breakdown",
  shortLabel: "Short (< 10 words)",
  mediumLabel: "Medium (10–25 words)",
  longLabel: "Long (> 25 words)",
  notableSentences: "Notable Sentences",
  /*
   * One message each. These were `Longest (` + count + ` words)`, so English rendered
   * "Longest (1 words)" and a translator saw only the two halves.
   */
  longestWords: "one: Longest ({count} word)|other: Longest ({count} words)",
  shortestWords: "one: Shortest ({count} word)|other: Shortest ({count} words)",
} as const;

function wordCount(sentence: string): number {
  return sentence.trim().split(RE_WORDS).filter(Boolean).length;
}

interface SentenceStats {
  count: number;
  avgWords: number;
  longestWords: number;
  shortestWords: number;
  longest: string;
  shortest: string;
  longCount: number;
  shortCount: number;
  mediumCount: number;
}

export function computeSentenceStats(text: string): SentenceStats {
  const sentences = splitSentenceStrings(text);
  if (sentences.length === 0) {
    return {
      count: 0,
      avgWords: 0,
      longestWords: 0,
      shortestWords: 0,
      longest: "",
      shortest: "",
      longCount: 0,
      shortCount: 0,
      mediumCount: 0,
    };
  }
  const counts = sentences.map(wordCount);
  const total = counts.reduce((a, b) => a + b, 0);
  const avg = Math.round((total / counts.length) * 10) / 10;
  const maxCount = Math.max(...counts);
  const minCount = Math.min(...counts);
  const maxIdx = counts.indexOf(maxCount);
  const minIdx = counts.indexOf(minCount);
  const longCount = counts.filter((c) => c > 25).length;
  const shortCount = counts.filter((c) => c < 10).length;
  const mediumCount = sentences.length - longCount - shortCount;
  return {
    count: sentences.length,
    avgWords: avg,
    longestWords: maxCount,
    shortestWords: minCount,
    longest: sentences[maxIdx] ?? "",
    shortest: sentences[minIdx] ?? "",
    longCount,
    shortCount,
    mediumCount,
  };
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

function truncate(text: string, max: number): string {
  if (text.length <= max) {
    return text;
  }
  return `${text.slice(0, max)}…`;
}

export default function SentenceCounterWidget() {
  const strings = useToolUi(SENTENCE_COUNTER_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [text, setText] = useState("");

  const stats = computeSentenceStats(text);

  return (
    <>
      <textarea
        aria-label={strings.textareaAriaLabel}
        className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        onChange={(e) => setText(e.target.value)}
        placeholder={strings.placeholder}
        value={text}
      />

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label={strings.sentences} value={fmt(stats.count)} />
        <StatCard
          label={strings.avgWordsPerSentence}
          value={fmt(stats.avgWords, { maximumFractionDigits: 1 })}
        />
        <StatCard
          label={strings.longestSentence}
          value={tPlural(strings.wordsCount, locale, stats.longestWords)}
        />
        <StatCard
          label={strings.shortestSentence}
          value={tPlural(strings.wordsCount, locale, stats.shortestWords)}
        />
      </div>

      {stats.count > 0 && (
        <>
          <section className="space-y-3">
            <h2 className="font-semibold text-foreground text-lg">
              {strings.sentenceLengthBreakdown}
            </h2>
            <div className="grid grid-cols-3 gap-4">
              <div className="rounded-lg border border-border bg-card p-4 text-center">
                <p className="font-semibold text-2xl text-foreground">
                  {fmt(stats.shortCount)}
                </p>
                <p className="mt-1 text-muted-foreground text-sm">
                  {strings.shortLabel}
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-4 text-center">
                <p className="font-semibold text-2xl text-foreground">
                  {fmt(stats.mediumCount)}
                </p>
                <p className="mt-1 text-muted-foreground text-sm">
                  {strings.mediumLabel}
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-4 text-center">
                <p className="font-semibold text-2xl text-foreground">
                  {fmt(stats.longCount)}
                </p>
                <p className="mt-1 text-muted-foreground text-sm">
                  {strings.longLabel}
                </p>
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="font-semibold text-foreground text-lg">
              {strings.notableSentences}
            </h2>
            <div className="space-y-3">
              <div className="rounded-lg border border-border bg-card p-4">
                <p className="mb-1 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                  {tPlural(strings.longestWords, locale, stats.longestWords)}
                </p>
                <p className="text-foreground text-sm leading-relaxed">
                  {truncate(stats.longest, 200)}
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-4">
                <p className="mb-1 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                  {tPlural(strings.shortestWords, locale, stats.shortestWords)}
                </p>
                <p className="text-foreground text-sm leading-relaxed">
                  {truncate(stats.shortest, 200)}
                </p>
              </div>
            </div>
          </section>
        </>
      )}
    </>
  );
}
